'use client';

import { type ChangeEvent, use, useEffect, useState } from 'react';

import { useAtom, useAtomValue } from 'jotai';
import { useRouter } from 'next/navigation';

import { getClientLocale } from '@cookies/GetClientLocale';
import { NUMBER_PLATE_REGEX } from '@core/Constants/Validations';
import type { VehicleValuationData } from '@core/Entities/Vehicle/Valuation.entity';
import { useGoogleRecaptcha } from '@core/Hooks/UseGoogleRecaptcha';
import { getReCaptchaToken } from '@core/Utils/loadGoogleRecaptcha';
import { PageLoader } from '@gds/PageLoader/PageLoader';
import { useModal } from '@modal/Hooks/UseModal';
import { tradeInAction } from '@shared-components/Actions/TradeIn/TradeIn.action';
import { myRegNumberAtom } from '@valuation/Atoms/myRegNumber.atom';
import { valuationAtom } from '@valuation/Atoms/Valuation.atom';

import type { DetailsInputFormProps } from './DetailsInputForm.entity';
import { InputSection } from './InputSection';
import { MotorwayModal } from './MotorwayModal/MotorwayModal';
import { ValuationSection } from './ValuationSection';

import styles from './DetailsInputForm.module.css';

const isMotorwayOption = (opt: string | null | undefined) => opt?.toLowerCase() === 'motorway';
const isPlpSearchOption = (opt: string | null | undefined) => opt?.toLowerCase() === 'plp_search';

export const DetailsInputForm = ({
  inputFormTitle,
  inputFormCtaOptions,
  stringsPromise,
  inputFormCtaCopy,
  valuationHeader,
  valuationCopy,
  valuationPrimaryCtaCopy,
  valuationPrimaryCtaOptions,
  valuationSecondaryCtaCopy,
  valuationSecondaryCtaOptions,
  valuationFooter,
  redirectTimerCopy,
  redirectModalHeader,
  redirectSubheader,
  redirectUspOne,
  redirectUspTwo,
  redirectUspThree,
  redirectErrorMessage,
  redirectLogo,
}: DetailsInputFormProps) => {
  const strings = use(stringsPromise);
  const regNumber = useAtomValue(myRegNumberAtom);

  const [mileage, setMileage] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isError, setIsError] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<boolean>(false);
  const router = useRouter();
  const [valuation, setValuation] = useAtom(valuationAtom);

  const { registration: registrationFromValuation } = valuation?.vehicle ?? {};
  const [reg, setReg] = useState<string>(registrationFromValuation || regNumber || '');
  const locale = getClientLocale();

  useGoogleRecaptcha(locale);

  const { openModal } = useModal();

  const [modalKey, setModalKey] = useState(0);

  const handleChange = (e: ChangeEvent<HTMLInputElement>, name: 'reg' | 'mileage') => {
    if (name === 'reg') setReg(e.target.value);
    if (name === 'mileage') setMileage(e.target.value);
  };

  const handleMileageStep = (delta: number) => {
    setMileage(prev => String(Math.min(999999, Math.max(0, Number(prev || 0) + delta))));
  };

  useEffect(() => {
    setReg(registrationFromValuation || regNumber || '');
  }, [regNumber, registrationFromValuation]);

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    setSubmitError(false);

    if (!reg || !mileage || !NUMBER_PLATE_REGEX.test(reg)) {
      setIsError(true);
      return false;
    }

    setIsLoading(true);
    setIsError(false);
    const recaptchaToken = (await getReCaptchaToken('trade_in')) as string;
    const res = await tradeInAction(reg, mileage, locale, recaptchaToken);

    if (res.error) {
      setSubmitError(true);
    } else {
      setValuation(res.data as VehicleValuationData);
    }

    setIsLoading(false);

    if (isMotorwayOption(inputFormCtaOptions)) {
      openMotorwayModal();
    }
  };

  const handleNotYourCarClick = () => {
    setValuation(null);
    setReg('');
    setMileage('');
    const valuationHero = document.getElementById('valuation-hero');
    if (valuationHero) {
      const top = valuationHero.getBoundingClientRect().top;
      window.scroll({
        top,
        behavior: 'smooth',
      });
    }
  };

  const handlePrimaryCtaClick = () => {
    if (isMotorwayOption(valuationPrimaryCtaOptions)) {
      openMotorwayModal();
    }
    if (isPlpSearchOption(valuationPrimaryCtaOptions)) {
      router.push('/cars/used');
    }
  };

  const handleSecondaryCtaClick = () => {
    if (isMotorwayOption(valuationSecondaryCtaOptions)) {
      openMotorwayModal();
    }
    if (isPlpSearchOption(valuationSecondaryCtaOptions)) {
      router.push('/cars/used');
    }
  };

  const openMotorwayModal = () => {
    setModalKey(prevKey => prevKey + 1);
    openModal(
      <MotorwayModal
        redirectTimerCopy={redirectTimerCopy}
        redirectSubheader={redirectSubheader}
        redirectUspOne={redirectUspOne}
        redirectUspTwo={redirectUspTwo}
        redirectUspThree={redirectUspThree}
        redirectErrorMessage={redirectErrorMessage}
        redirectLogo={redirectLogo}
        reg={reg}
        modalKey={modalKey}
      />,
      {
        header: redirectModalHeader,
      },
    );
  };

  const valuationProps = {
    valuationHeader,
    valuationCopy,
    valuationFooter,
    valuationPrimaryCtaCopy,
    valuationSecondaryCtaCopy,
    locale,
    strings,
    handlePrimaryCtaClick,
    handleSecondaryCtaClick,
    handleNotYourCarClick,
  };

  const inputProps = {
    inputFormTitle,
    errorMessage: isError
      ? strings.invalidRegNumber
      : submitError
        ? strings.valuationError
        : undefined,
    strings,
    reg,
    mileage,
    isLoading,
    inputFormCtaCopy,
    onSubmit,
    setIsError,
    handleChange,
    onMileageStep: handleMileageStep,
  };

  return (
    <div>
      {valuation && <ValuationSection {...valuationProps} valuation={valuation} />}
      {valuation === null && <InputSection {...inputProps} />}
      {valuation === undefined && (
        <div className={styles.pageLoader}>
          <PageLoader isAbsolute />
        </div>
      )}
    </div>
  );
};
